import { Component, OnInit, ViewChild, ElementRef, Input, OnDestroy, inject, AfterViewInit } from '@angular/core';
import { CommonModule, Location, AsyncPipe } from '@angular/common';
import { catchError, combineLatest, filter, map, mergeMap, of, Subject, take, takeUntil, throwError } from 'rxjs';
import { Overlay, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { CdkDrag, CdkDragEnd, DragDropModule, DragRef } from '@angular/cdk/drag-drop';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DOC_ORIENTATION, NgxImageCompressService } from 'ngx-image-compress';

import { ServiceService } from '../service.service';
import { StoryElementInterface, StoryService } from './story.service';
import { StoriesService } from '../stories.service';
import { LayoutService } from '../layout.service';
import { HomeService } from '../home/home.service';
import { PhotoService } from '../photo/photo.service';

import { ConfirmDialogComponent } from '../dialog/confirm-dialog/confirm-dialog.component';
import { ImagesHolderComponent } from './images-holder/images-holder.component';
import { ToolbarEditStoryComponent } from './toolbar/toolbar-edit-story/toolbar-edit-story.component';
import { ResizeStoryElementComponent } from './resize-story-element/resize-story-element.component';
import { SaveImageDialogComponent } from '../dialog/save-image-dialog/save-image-dialog.component';
import { PublishStoryDialogComponent } from '../dialog/publish-story-dialog/publish-story-dialog.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { ExitEditStoryDialogComponent } from '../dialog/exit-edit-story-dialog/exit-edit-story-dialog.component';

import { Story } from '../shared/interfaces/story';
import { Color } from '../shared/interfaces/color';

import { InitialStoryPhotoArray, PhotoObject } from './images-holder/story-photos';
import { FastAverageColor } from 'fast-average-color';
import { dataURLtoFileConverter } from '../shared/tools/dataUrlToFileConverter';

@Component({
  selector: 'app-create-story',
  standalone: true,
  imports: [
    CommonModule,
    ConfirmDialogComponent,
    ImagesHolderComponent,
    ToolbarEditStoryComponent,
    OverlayModule,
    PortalModule,
    CdkDrag,
    DialogModule,
    DragDropModule,
    AsyncPipe,
    LoaderComponent
  ],
  templateUrl: './create-story.component.html',
  styleUrl: './create-story.component.scss',
})
export class CreateStoryComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() update: Story | null = null
  @Input() homeStoryImages: PhotoObject[] = []
  @ViewChild('storyPhoto') storyPhoto!: ElementRef<HTMLImageElement>
  @ViewChild('textContainer') textContainer!: ElementRef  
  @ViewChild('imageContainer') imageContainer!: ElementRef
  @ViewChild(ToolbarEditStoryComponent) toolbarEditStory!: ToolbarEditStoryComponent
  @ViewChild('mobilePhotoHolder') mobilePhotoHolder!: ElementRef
  @ViewChild('desktopPhotoHolder') desktopPhotoHolder!: ElementRef
  @ViewChild('mobileSection') mobileSection!: ElementRef
  @ViewChild(ImagesHolderComponent) imagesHolder!: ImagesHolderComponent
  @ViewChild('draggable') draggable!: DragRef

  private _destroyed = new Subject<void>()
  currentStoryElement!: StoryElementInterface
  dialog = inject(Dialog);
  private canvas! : HTMLCanvasElement
  ctx!: CanvasRenderingContext2D;
  showPortal = false
  awaitingImage = false
  lines: string[] = [];
  componentPortal!: ComponentPortal<ResizeStoryElementComponent>;
  hideMenu = false
  allStoryElements$ = this.storyService.allStoryElements$
  photos = [...InitialStoryPhotoArray]
  storyVisuals: StoryElementInterface[] = []
  currentPhoto!: StoryElementInterface
  photoIndex: number = 0
  colors: Color[] = []
  dragItemEnded = false
  desktopSectionPosition!: { top: number, left: number }
  screenSize!: { height: number, width: number }
  warning = false
  dialogInfo!: {
    title: string,
    content: string
  }
  storyId!: number
  isDesktop = this.layoutService.isDesktop
  storyToEdit!: Story | null
  loading = true
  storyEditionMode = false
  resetArray = JSON.parse(JSON.stringify([...InitialStoryPhotoArray]))
  resizeImage = false
  storyImages: PhotoObject[] = []

  constructor(
    private serviceService: ServiceService,
    private storyService: StoryService,
    private layoutService: LayoutService,
    private overlay: Overlay,
    private location: Location,
    private storiesService: StoriesService,
    private router: Router,
    private homeService: HomeService,
    private route: ActivatedRoute,
    private imageCompress: NgxImageCompressService,
    private photoService: PhotoService
  ) { }

  ngOnInit() {
    this.screenSize = {
      height: window.innerHeight,
      width: window.innerWidth
    }

    // stream of emojis/text used for a single photo
    this.storyService.latestStoryElement.pipe(takeUntil(this._destroyed)).subscribe(res => {
      this.currentStoryElement = res
      this.open()
    })

    // case of story edition
    if(this.route.snapshot.params['id']){
      this.storyEditionMode = true
      this.homeService.toggleSidenav.next(true)

      // cas où on vient de My Stories
      this.storyService.story.subscribe(res => {
        if(!res || !res.id) return
        this.storyId = res.id
      })

      this.storyService.storyImages.subscribe(res => {
        this.photos = res
        
        if(!this.photos.length) return

        this.currentPhoto = {src: this.photos[0].src}
        this.awaitingImage = true
        this.loading = false

        if(this.storyEditionMode) this.homeService.pageDisplayed.next('edit-story')
      })

      // cas d'un refresh 
      if(!this.photos[0].src) {
        this.storiesService.getStoryById(this.route.snapshot.params['id']).subscribe(story => {
          this.storyId = story.id!
          const photos: PhotoObject[] = []

          story.photos.forEach((_, index) => {
            photos.push({ src: story.photos[index].url, saved: true })
          })
  
          if(story.photos.length < 5){
            for(let i = story.photos.length; i < 5; i++){
              photos.push({
                src: null,
                disabled: i === story.photos.length ? false : true,
                saved: false
              })
            }
          }
  
          this.storyService.currentPhoto.next({src: photos[0].src})
          this.currentPhoto.src = photos[0].src
          this.awaitingImage = true
          this.loading = false
          this.storyService.storyImages.next(photos)
        })
      }
    } else {
      //cas d'une création de story
      this.storiesService.createStory().pipe(
        take(1),
        map(id => {
          this.storiesService.storyId = id
          this.storyId = id
          this.loading = false
          this.awaitingImage = true
        }),
        mergeMap(() => {
          return this.storyService.storyImages.pipe(
            map((res) => {
              this.photos = res
              this.currentPhoto = res[0]
              // if no image selected, redirect to home
              if(res[0].src === null) this.router.navigate(['/home'])
              return res
            }),
            catchError((error) => {
              return throwError(() => error)
            }),
          );
        })
      ).subscribe()
    }

    //color palette for text editing
    this.serviceService.getColors().subscribe(res => this.colors = res)

    // this.storyService.currentPhoto.subscribe(res => {
    //   this.currentPhoto = res
    //   if(this.currentPhoto) this.currentPhoto.type = "visual"
    // })

    if(this.currentPhoto) this.currentPhoto.type = "visual"
  }

  ngAfterViewInit(): void {
    this.canvas = <HTMLCanvasElement>document.getElementById("canvasSource")
    this.ctx = this.canvas.getContext("2d") as CanvasRenderingContext2D
  }

  onImageLoad(){
    console.log('onImageLoad')
    this.currentPhoto = {src: this.currentPhoto.src!, x: 0, y: 0, angle: 0, type: 'visual', width: this.storyPhoto.nativeElement.naturalWidth, height: this.storyPhoto.nativeElement.naturalHeight }
        
    if(!this.currentPhoto.saved){
      const img = new Image()

      img.src = this.storyPhoto.nativeElement.src

      img.onload = () => {
        const canvas = <HTMLCanvasElement>document.getElementById("canvasSource");

        if(img.naturalWidth >= img.naturalHeight){
          this.canvas.width = img.naturalWidth
          this.canvas.height = img.naturalWidth * 1.4
        } else if(img.naturalWidth < img.naturalHeight){
          this.canvas.width = img.naturalHeight / 1.4
          this.canvas.height = img.naturalHeight
        } 

        // this.canvas.width = img.naturalWidth
        // this.canvas.height = img.naturalHeight
        const context = canvas.getContext("2d");

        if (context) this.ctx = context;
      }

      // const canvas = <HTMLCanvasElement>document.getElementById("canvasSource");
      // canvas.width = this.storyPhoto.nativeElement.width
      // canvas.height = this.storyPhoto.nativeElement.width * 1.4
      // const context = canvas.getContext("2d");

      //if (context) this.ctx = context;
    }
  }

  get parentTextBoxStyles() {
    const textElement = this.storyVisuals.find(el => el.type === 'text')

    if(!textElement) return
    if(!textElement.x) textElement.x = 0
    if(!textElement.y) textElement.y = 0

    return {
      'position': 'absolute',  
      'display': 'block',
      'left': textElement.x + 'px' || '',
      'top': textElement.y + 'px' || '',
      'width': 'fit-content',//textElement.width + 'px',
      'height': 'fit-content'//textElement.height + 'px',
    }
  }

  get textBoxStyles() {
    const textElement = this.storyVisuals.find(el => el.type === 'text')

    return {
      'display': 'inline-block',
      'vertical-align': 'top',
      'background': 'transparent',
      'font-size.px': textElement?.fontSize,
      'transform': `rotate(${textElement?.angle}deg)`,
      'color': textElement?.color,
      'max-width.px': textElement?.width,
      'text-align': 'left',
    }
  }

  dragEnded(ev: CdkDragEnd, index: number) {
    console.log('dragEnded')
    this.dragItemEnded = true
    //measure the distance from previous to current location of the element to calculate x, y coordinates
    const containerDomrect = ev.source.element.nativeElement.parentElement?.getBoundingClientRect()
    const elementDomrect = ev.source.element.nativeElement.getBoundingClientRect()
    const imagesHolderHeight = 140

    if(!containerDomrect) return
    if(!containerDomrect.x) containerDomrect.x = 0
    if(!containerDomrect.y) containerDomrect.y = 0

    ev.source.setFreeDragPosition({ x: 0, y: 0 })

    let xCoord = 0
    if(elementDomrect.x < containerDomrect.x){
      // exceeds on left side
      xCoord = 0
    } else if((elementDomrect.x + elementDomrect.width) > (containerDomrect.x + containerDomrect.width)){
      // exceeds on right side
      xCoord = containerDomrect.width - elementDomrect.width
    } else {
      xCoord = elementDomrect.x - containerDomrect.x
    }

    let yCoord = 0
    if(this.isDesktop()){
      if(elementDomrect.y < containerDomrect.y){
        // exceeds at the top
        yCoord = 0
      } else if((elementDomrect.y + elementDomrect.height) > ((containerDomrect.y + containerDomrect.height) - imagesHolderHeight)){
        // exceeds at the bottom
        yCoord = (containerDomrect.y + containerDomrect.height) - imagesHolderHeight - elementDomrect.height
      } else {
        yCoord = elementDomrect.y - containerDomrect.y
      }
    } else {
      if(elementDomrect.y < containerDomrect.y){
        // exceeds at the top
        yCoord = 0
      } else if((elementDomrect.y + elementDomrect.height) > ((containerDomrect.y + containerDomrect.height))){
        // exceeds at the bottom
        yCoord = containerDomrect.height - elementDomrect.height
      } else {
        yCoord = elementDomrect.y - containerDomrect.y
      }
    }

    //update coordinates of draggable item relative to the photo
    this.storyVisuals[index].x = xCoord
    this.storyVisuals[index].y = yCoord

    //console.log("after",this.storyVisuals)
  }

  open(event: MouseEvent | null = null, el: StoryElementInterface | null = null, index: number | null = null) {
    console.log('open')

    if(this.isDesktop()){
      if (this.dragItemEnded){
        this.dragItemEnded = false
        return
      }
    }

    const textIndex = this.storyVisuals.findIndex(element => element.type === 'text')

    if (textIndex !== -1 && this.currentStoryElement?.type === 'text') {
      //only 1 text element means that the text element is removed from the array of story elements
      this.storyVisuals.splice(textIndex, 1)
      this.storyService.allStoryElements.next(this.storyVisuals)
    }

    let emoji = this.currentStoryElement
    event?.stopPropagation()

    if (el) {
      emoji = el
      //remove the element from the array of story elements
      this.storyVisuals.splice(index as number, 1)
    }

    //create portal and overlay instance to inject the resize component
    this.componentPortal = new ComponentPortal(ResizeStoryElementComponent);

    let config: OverlayConfig
    if(this.isDesktop()){
       config = new OverlayConfig({
        positionStrategy: this.overlay.position().global().centerHorizontally(),
        hasBackdrop: true,
        width: '100%',
        height: '100%',
        maxHeight: `${this.screenSize.height}px`,
        maxWidth: `${this.screenSize.height / 1.4}px`,
        scrollStrategy: this.overlay.scrollStrategies.block() // à revoir
      })
    } else {
       config = new OverlayConfig({
        hasBackdrop: true,
        width: '100%',
        height: '100%'
      })
    }
    
    const overlayElement = this.overlay.create(config)
    const overlayWithAttachment = overlayElement.attach(this.componentPortal)

    //set the instances in the resize component
    overlayWithAttachment.instance.currentElement = emoji;
    overlayWithAttachment.instance.componentHeight = this.screenSize.width * 1.4;
    overlayWithAttachment.instance.componentWidth = this.screenSize.width;
    emoji && emoji.width && overlayWithAttachment.instance.scale.setValue(emoji.type === 'visual' ? emoji.width! : emoji.fontSize!);
    emoji && emoji.angle && overlayWithAttachment.instance.rotate.setValue(emoji.angle);

    overlayWithAttachment.instance.addElement.subscribe((data: StoryElementInterface) => {
      //replace the thumb sized emojis with full size emojis and centre on screen
      if(!data) return
      if(!data.width) data.width = 0
      if(!data.height) data.height = 0

      // set initial position for the visual element
      if(this.isDesktop()){
        console.log(this.canvas.height, this.canvas.width)
        console.log(this.screenSize.height, this.screenSize.width)
        console.log(data)
        this.storyVisuals.push({ ...data, src: data.src && data.src?.replace('thumb', 'original'), x: ((this.screenSize.height / 1.4) / 2 - data.width / 2), y: ((this.screenSize.height) / 2 - data.height / 2) })
      } else {
        this.storyVisuals.push({ ...data, src: data.src && data.src?.replace('thumb', 'original'), x: (this.screenSize.width / 2 - data.width / 2), y: ((this.screenSize.width * 1.4) / 2 - data.height / 2) })
      }

      //console.log(this.storyVisuals)
      this.storyService.allStoryElements.next(this.storyVisuals)
    })

    //when finished editing the element, remove the overlay and demount the component
    overlayWithAttachment.instance.closePanel.subscribe(() => {
      overlayElement.detach();
      this.hideMenu = false
    });

    this.hideMenu = true
  }

  openResizeEl() {
    this.currentStoryElement = this.storyVisuals.find(el => el.type === 'text')!
    this.open()
  }

  openDialog(e: {type: string, imageToSave?: boolean}){
    // if imageToSave, openSaveDialog()
    // if !imageToSave: 
    // if click on add image button => open directly file input
    // if click on submit button => openPublishDialog()
    if(e.type === 'save' && e.imageToSave) {
      this.openSaveDialog()
    } else if(e.type === 'publish' && e.imageToSave) {
      this.openSaveDialog('publish')
    } else {
      this.openPublishDialog()
    }
  }
  
  // to save an image
  openSaveDialog(action?: string) {
    console.log('openSaveDialog')
    this.dialog.open(SaveImageDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res),
    ).subscribe(() => {
      this.awaitingImage = false
      //create canvas and draw the image and text on it then automatically trigger selecting a new photo
      this.saveImage()
      if(action === 'publish'){
        this.dialog.closeAll()
        this.openPublishDialog()
      } else {
        this.imagesHolder.fileUpload.nativeElement.click()
      }
    })
  }

  goBack(){
    this.location.back()
  }

  // to exit editing mode
  openExitEditStoryDialog() {
    console.log('openExitEditStoryDialog')
    this.dialog.open(ExitEditStoryDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
    }).closed.pipe(
      filter(res => !!res),
    ).subscribe(() => {
      // reset the story
      this.storyService.story.next(null)
      this.storyService.storyImages.next([])
      this.storyEditionMode = false
      this.homeService.pageDisplayed.next('my-stories') 
      this.location.back()      
    })
  }

  openPublishDialog(){
    let storyPhotos = 0
    this.photos.forEach(image => {
      if(image.src) storyPhotos++
    })
    this.dialog.open(PublishStoryDialogComponent, {
      minWidth: '300px',
      maxWidth: '450px',
      width: '100vw',
      data: storyPhotos !== 5
    }).closed.subscribe((res) => {
      if(res === 'add'){ // add a new image to the story
        this.dialog.closeAll()
        // open the explorer to select image
        this.storyService.openFileInput.next(true)
      } else if(res === 'save') { // put on hold
        this.storyService.storyImages.next(this.resetArray)
        
        if(!this.storyEditionMode){
          this.router.navigate(['/home'])   
          this.storiesService.storyMessage.next({origin: 'create', type: 'onHold'})
        } else {
          this.goBack()
          this.storiesService.storyEditionClosed.next(true)
          this.storiesService.storyMessage.next({origin: 'edit', type: 'onHold'})
        }
      } else if(res === 'publish'){ // post it
        if(this.storyEditionMode && !this.allStoryElements$) return

        this.storiesService.publishStory(this.storyId).subscribe(() => {
          if(!this.storyEditionMode) {
            this.router.navigate(['/home'])
            this.storiesService.storyMessage.next({origin: 'create', type: 'posted'})
          } else {
            // exit without saving 
            this.goBack()
            this.storiesService.storyEditionClosed.next(true)
            this.storiesService.storyMessage.next({origin: 'edit', type: 'posted'})

            // reset the story
            this.storyService.story.next(null)
            this.storyService.storyImages.next(this.resetArray)
          }
        })
      }
    })
  }

  getStoryPhotoStyle(){
    let ratio = 1
    if(this.currentPhoto) { 
      ratio = this.currentPhoto.height! / this.currentPhoto.width! 
    }
    this.storyService.ratio = ratio

    if(ratio > 1.4){
      return {
        width: 'auto',
        height: '100%'
      }
    } else {
      return {
        height: 'auto',
        width: '100%'
      }
    }    
  }

  getImageWrapperStyle(){
    if(this.isDesktop()){
      return {
        width: `${this.screenSize.height / 1.4}px`,
        height: `${this.screenSize.height}px`
      }
    } else {
      return {
        width: `${this.screenSize.width}px`,
        height: `${this.screenSize.width * 1.4}px`
      }
    }
  }

  emptyStoryVisuals(imageDeleted: boolean){
    if(imageDeleted){
      this.storyVisuals = []
      this.storyService.allStoryElements.next([])
    }
  }

  canvasText(element: StoryElementInterface) {
    const lineInfo = this.ctx.measureText(element.content!)
    const lineHeight = (lineInfo.fontBoundingBoxAscent + lineInfo.fontBoundingBoxDescent)
    return {lineHeight, lineInfo}
  }

  wrapText(element: StoryElementInterface) {
    //console.log(element)
    if(!element.content) element.content = ''
    if(!element.color) element.color = '#FFF'
    
    // texts can be separated by new line or space
    const words: string[] = element.content.replace(/\n/g, " ").split(" ")
    const lines: string[] = []
    let ratio = 1
    if(this.isDesktop()){
      ratio = this.canvas.height / this.screenSize.height
    } else {
      ratio = this.canvas.height / (this.screenSize.width * 1.4)
    }
    
    if(!element.fontSize) return
    this.ctx.font = `${element?.fontSize * ratio}px Montserrat`
    this.ctx.fillStyle = element.color
    this.ctx.textBaseline = "top"
    this.ctx.textAlign = "left"
    this.ctx.direction = "ltr"
    const {lineHeight, lineInfo} = this.canvasText(element)
    let currentLine = words![0]

    // multiline text wrapping
    for (let i = 1; i < words.length; i++) {
      const word = words[i]
      const width = this.ctx.measureText(currentLine + " " + word).width

      if (width < element.width! * ratio) {
          currentLine += " " + word
      } else {
          lines.push(currentLine)
          currentLine = word
      }
    }

    lines.push(currentLine)

    if(this.isDesktop()){
      if (element.angle ?? 0 > 0) {
        this.ctx.save()
        this.ctx.translate(element.x!* ratio + element.width! / 2, element.y! * ratio + element.height! / 2)
        this.ctx.rotate(element.angle! * Math.PI / 180)
        lines.forEach((line, index) => {
          this.ctx.fillText(line, -element.width! / 2, -element.height! / 2 + (index * lineHeight))
        })
      
        this.ctx.restore()
      } else {
        let ycoord = element.y! * ratio + lineInfo.fontBoundingBoxAscent
        lines.forEach((line) => {
          console.log(element.x, ycoord)
          this.ctx.fillText(line, element.x!* ratio, ycoord)
          ycoord += lineHeight
        })
      }
    } else {
      if (element.angle ?? 0 > 0) {
        this.ctx.save()
        this.ctx.translate(element.x!* ratio + element.width! / 2, element.y! * ratio + element.height! / 2)
        this.ctx.rotate(element.angle! * Math.PI / 180)
        lines.forEach((line, index) => {
          this.ctx.fillText(line, -element.width! / 2, -element.height! / 2 + (index * lineHeight))
        })
      
        this.ctx.restore()
      } else {
        let ycoord = element.y! * ratio + lineInfo.fontBoundingBoxAscent / ratio
        lines.forEach((line) => {
          console.log(element.x, ycoord)
          this.ctx.fillText(line, element.x!* ratio, ycoord)
          ycoord += lineHeight
        })
      }
    }
  }

  saveImage() {
    console.log('saveImage')
    const imagesList = [this.currentPhoto, this.storyVisuals].flat().filter(el => el?.type === 'visual')

    if(imagesList.length > 4) {
      console.error('You cannot add more than 5 images to a story.')
      return
    }

    const textElement = this.storyVisuals.find(el => el.type === 'text') || null

    //once all the images are loaded, draw them on the canvas finishing with the text element
    const firstImagePromise = new Promise<void>((resolve) => {
      const img = new Image()
      img.src = imagesList[0]?.src || '' // imagesList[0] is background image
      //console.log(img, this.ctx)

      this.photos[this.photoIndex].src = img.src
      this.storyService.storyImages.next(this.photos)

      img.onload = () => {
        const ratio = img.width / img.height

        if(this.isDesktop()){
          // the size of the image drawn depends on the original image ratio
          if(this.storyService.ratio < 1.4){
            imagesList[0].width = this.canvas.width
            imagesList[0].height = this.canvas.width * this.storyService.ratio
          } else {
            imagesList[0].height = this.canvas.height
            imagesList[0].width = this.canvas.height / this.storyService.ratio
          }

          // center image if smaller than canvas
          if(imagesList[0].width! < this.canvas.width){
            imagesList[0].x = (this.canvas.width - imagesList[0].width!) / 2
          } else {
            imagesList[0].x = 0
          }
          
          if(imagesList[0].height! < this.canvas.height){
            imagesList[0].y = (this.canvas.height - imagesList[0].height!) / 2
          } else {
            imagesList[0].y = 0
          }
        } else {
          // image is higher than wide
          if(ratio < 1 && this.storyService.ratio > 1.4){
            imagesList[0].height = this.canvas.height
            imagesList[0].width = this.canvas.height / this.storyService.ratio
            imagesList[0].x = (this.canvas.width - imagesList[0].height / this.storyService.ratio) / 2
          } else { //ratio === 1 || ratio > 1 || (ratio < 1 && imageRatio < 1.4)
            // image is wider than high
            imagesList[0].width = this.canvas.width
            imagesList[0].height = this.canvas.width * this.storyService.ratio
            imagesList[0].y = (this.canvas.height - imagesList[0].width * this.storyService.ratio) / 2
          }
        }

        this.ctx.save()
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        this.ctx.drawImage(img, imagesList[0]?.x!, imagesList[0]?.y!, imagesList[0]?.width!, imagesList[0]?.height!)
        this.ctx.restore()

        // calculate an average color based on background image hues
        let color = ''
        const fac = new FastAverageColor()
        color = fac.getColor(this.canvas).hex
        
        this.ctx.fillStyle = color
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
        
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        this.ctx.drawImage(img, imagesList[0]?.x!, imagesList[0]?.y!, imagesList[0]?.width!, imagesList[0]?.height!)
        this.ctx.restore()
        
        resolve()
      }
    })
  
    // treatment of visuals
    const imagePromises = imagesList.map((image, index) => {
      if(index === 0) return // excludes imagesList[0] which is the background image

      return new Promise<void>((resolve) => {
        const img = new Image()
        img.src = image?.src || ''

        img.onload = () => {
          let ratioCanvasVsScreen = 1
          if(this.isDesktop()){
            ratioCanvasVsScreen = this.canvas.height / this.screenSize.height
          } else {
            ratioCanvasVsScreen = this.canvas.height / (this.screenSize.width * 1.4)
          }
          
          if(!image.angle) image.angle = 0

          if(image?.angle ?? 0 > 0){
            this.ctx.save()
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            this.ctx.translate((image.x! * ratioCanvasVsScreen) + (image.width! * ratioCanvasVsScreen) / 2, (image.y! * ratioCanvasVsScreen) + (image.height! * ratioCanvasVsScreen) / 2)
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            this.ctx.rotate(image?.angle! * Math.PI / 180)
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            this.ctx.drawImage(img, -(image?.width! * ratioCanvasVsScreen)/2, -(image?.height! * ratioCanvasVsScreen)/2, image?.width! * ratioCanvasVsScreen, image?.height! * ratioCanvasVsScreen)
            this.ctx.restore()
          } else {
            this.ctx.save()
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            this.ctx.drawImage(img, image?.x! * ratioCanvasVsScreen, image?.y! * ratioCanvasVsScreen, image?.width! * ratioCanvasVsScreen, image?.height! * ratioCanvasVsScreen)
            this.ctx.restore()
          }
          
          resolve()
        };
      });
    });

    // treatment of background image
    firstImagePromise.then(() => {
      Promise.all(imagePromises).then(() => {
        // treatment of text element
        if (textElement) {
          this.wrapText(textElement)
        }
      }).then(() => {
        const dataURL = this.canvas.toDataURL("image/jpeg")
        // update the photo object and reset all the variables
        this.photos[this.photoIndex].src = dataURL
       
        // generate a file from the dataUrl
        const file = dataURLtoFileConverter(dataURL,'');
        
        // add image to story
        const img = new Image()
        img.onload = () => {
          const dataUrlArr = dataURL.split(',')
          const data: {
            origin: string, //destination image in base64
            mime: string
          } = {
            origin: dataUrlArr[1],
            mime: dataUrlArr[1][0] === 'i' ? 'png' : 'jpeg'
          }

          this.storiesService.addImageToStory(data, this.storyId).pipe(
            mergeMap((res: { photo: { id: number, signed_url: string, url: string }}) => {
              return combineLatest([of(res), this.photoService.sendPhotoToSignedUrl(res.photo.signed_url, file!, data.mime)])
            })
          ).subscribe(() => {
            this.photos[this.photoIndex].saved = true
            this.photos[this.photoIndex].edited = true
            this.photos[this.photoIndex].src = img.src
            this.awaitingImage = true
            this.storyVisuals = []
            this.storyService.allStoryElements.next([])
            this.storyService.storyImages.next(this.photos)

            console.log(this.storyService.storyImages.value)
            this.currentPhoto = {src: img.src}
            this.storyService.currentPhoto.next({src: img.src})
          })
        }

        img.src = dataURL

        // create a thumbnail version of the image for the images holder
        const thumbnail = new Image()
        this.imageCompress.compressFile(dataURL as string, DOC_ORIENTATION.Default, 100, 100, 100, 100).then((r)=>{
          thumbnail.src = r

          this.photos[this.photoIndex].thumbnailSrc = thumbnail.src
        })
      })
    })
  }
  
  currentImage(story: { image: PhotoObject, index: number }) {
    this.currentPhoto = { src: story.image.src || null, x: 0, y: 0, angle: 0, type: 'visual' }
    this.photoIndex = story.index
  }

  showCloseDialog(){
    this.warning = true
    this.dialogInfo = {
      title: $localize `:Title for the dialog when deleting a story from the story creation module@@REMOVE_STORY:Are you sure you wish to remove this story?`,
      content: $localize `:Description for the dialog when deleting a story from the story creation module@@REMOVE_STORY2:This story will be permanently deleted from your profile.`
    }
  }

  confirmStoryDeletion(close: boolean){
    this.warning = false
    if(close) this.location.back()
  }

  ngOnDestroy(): void {
    this.storyService.story.next(null)
    this.storyService.storyImages.next(this.resetArray)
    this._destroyed.next()
    this._destroyed.complete()
  }
}
